import { Mail } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { ContactForm } from "@/components/contact/ContactForm";
import { PATHS } from "@/lib/content";
import { usePathSelector } from "@/context/PathSelectorContext";
import { useContactInfo } from "@/hooks/useContactForm";
import SEO from "@/components/common/SEO";

const ContactPaths = () => {
  const { openSelector } = usePathSelector();

  return (
    <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
      <div className="grid gap-6 md:grid-cols-3">
        {PATHS.map((path, index) => (
          <Reveal key={path.id} delay={index * 0.08}>
            <button
              onClick={() => openSelector(path.id)}
              data-testid={`contact-path-${path.id}`}
              className="group flex h-full w-full flex-col items-start rounded-2xl border border-border bg-white p-8 text-left transition-[background-color,border-color] duration-200 hover:border-blue-600 hover:bg-blue-50"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                <Icon name={path.icon} className="h-6 w-6" />
              </span>
              <span className="mt-6 font-heading text-xl font-bold text-slate-900">
                {path.label}
              </span>
              <span className="mt-2 text-sm leading-relaxed text-slate-500">
                {path.blurb}
              </span>
              <span className="mt-6 text-sm font-semibold text-blue-600">
                Start here →
              </span>
            </button>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function Contact() {
  const email = useContactInfo();

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Success Crest Consulting | Recruitment & Consulting"
        description="Contact Success Crest Consulting for recruitment, HR consulting, career services, AI and digital solutions, or general business enquiries."
        path="/contact"
      />

      <PageHeader
        overline="Contact"
        title="Tell us what you need."
        subtitle="We don't start with a generic form. Pick the description that fits you, and we'll guide you through the right questions from there."
      />

      <ContactPaths />

      <section
        className="border-t border-border bg-blue-50 py-20 lg:py-24"
        data-testid="contact-general"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20 lg:px-8">
          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
              General enquiries
            </h2>
            <p className="mt-4 max-w-md text-slate-600">
              Prefer to send a message directly? Use the form and we'll get back
              to you.
            </p>

            {email && (
              <a
                href={`mailto:${email}`}
                data-testid="contact-email"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700"
              >
                <Mail className="h-4 w-4" /> {email}
              </a>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      <style>{`
        .scc-cinput { width:100%; border:1px solid hsl(214 32% 91%); border-radius:.75rem; background:#fff; padding:.75rem 1rem; font-size:.95rem; color:#111827; transition:border-color .2s ease, box-shadow .2s ease; }
        .scc-cinput:focus { outline:none; border-color:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,.15); }
      `}</style>
    </div>
  );
}
